import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Button,
  TextField,
  Paper,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Tabs,
  Tab,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  Fab,
} from '@mui/material';
import {
  TrendingUp,
  AttachMoney,
  ShoppingCart,
  People,
  Download,
  Refresh,
  FilterList,
  Monitor,
  Upload,
  AdminPanelSettings,
  SupervisorAccount,
  Security,
  AccountBalance,
  Inventory,
  Menu as MenuIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { analyticsService } from '../services/analyticsService';
import AsyncSearchSelect from '../components/AsyncSearchSelect';
import { financeService } from '../services/financeService';
import { withCache } from '../services/cacheService';
import { useNotification } from '../contexts/NotificationContext';
import { useAuth } from '../contexts/AuthContext';

// Importar novos componentes de gráficos
import TrafficSourcePieChart from '../components/charts/TrafficSourcePieChart';
import HourlyPatternChart from '../components/charts/HourlyPatternChart';
import CampaignPerformanceChart from '../components/charts/CampaignPerformanceChart';
import GeographicChart from '../components/charts/GeographicChart';
import TemporalAnalysisChart from '../components/charts/TemporalAnalysisChart';
import CSVUploadComponent from '../components/CSVUploadComponent';
import DailySalesTable from '../components/charts/DailySalesTable';
import WebhookMonitoring from '../components/WebhookMonitoring';

const DashboardPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  // Legacy product preload removed; using AsyncSearchSelect with server-side fetch
  const [currentTab, setCurrentTab] = useState(0); // 0 = Dashboard, 1 = Monitoramento, 2 = CSV Upload
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [filters, setFilters] = useState({
    startDate: null,
    endDate: null,
    productId: '',
    productTitle: '', // Mantém para exibição
    utmSource: '',
    utmCampaign: '',
    utmMedium: '',
  });

  const { showError, showSuccess, showLoading } = useNotification();
  const { user, hasPermission, isAdmin, isSuperAdmin } = useAuth();
  const navigate = useNavigate();

  // Carregar produtos disponíveis (usando endpoint leve)
  // Removed legacy loadAvailableProducts (now fetched on-demand by AsyncSearchSelect)

  // Carregar dados do dashboard com cache inteligente
  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Usar cache com chave específica baseada nos filtros
      const cacheKey = `dashboard_analytics_${JSON.stringify(filters)}`;
      
      const data = await withCache(cacheKey, async () => {
        return await analyticsService.getDashboardAnalytics(filters);
      }, 10 * 60 * 1000); // Cache de 10 minutos para dashboard
      
      setDashboardData(data);
    } catch (err) {
      setError(err.message || 'Erro ao carregar dados do dashboard');
      showError('Erro ao carregar dados do dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Carregar dados iniciais
  useEffect(() => {
    loadDashboardData();
  }, []);

  // Aplicar filtros
  const handleApplyFilters = () => {
    loadDashboardData();
  };

  // Limpar filtros
  const handleClearFilters = () => {
    setFilters({
      startDate: null,
      endDate: null,
      productId: '',
      productTitle: '',
      utmSource: '',
      utmCampaign: '',
      utmMedium: '',
    });
  };

  // Gerar relatório PDF
  const handleGenerateReport = async () => {
    try {
      showLoading('Gerando relatório...');
      await analyticsService.downloadReport(filters, 'Relatório Dashboard BI');
      showSuccess('Relatório gerado com sucesso!');
    } catch (err) {
      showError('Erro ao gerar relatório');
    }
  };

  // Formatação de valores
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value || 0);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('pt-BR').format(value || 0);
  };

  // Funções do drawer administrativo
  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleNavigate = (path) => {
    navigate(path);
    setDrawerOpen(false);
  };

  // Configuração dos itens do menu administrativo
  const adminMenuItems = [
    {
      text: 'Usuários',
      icon: <SupervisorAccount />,
      path: '/admin/users',
      permission: 'users.read',
      description: 'Gerenciar usuários do sistema'
    },
    {
      text: 'Roles & Permissões',
      icon: <Security />,
      path: '/admin/roles',
      permission: 'roles.read',
      description: 'Configurar papéis e permissões'
    },
    {
      text: 'Financeiro',
      icon: <AccountBalance />,
      path: '/admin/finance/transactions',
      permission: 'finance.transactions.read',
      description: 'Gestão financeira e transações'
    },
    {
      text: 'Inventário',
      icon: <Inventory />,
      path: '/admin/assets',
      permission: 'inventory.assets.read',
      description: 'Controle de ativos e estoque'
    }
  ];

  // Filtrar itens baseado nas permissões do usuário
  const availableMenuItems = adminMenuItems.filter(item => {
    return isAdmin() || isSuperAdmin() || hasPermission(item.permission);
  });

  const hasAdminAccess = availableMenuItems.length > 0;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" action={
        <Button color="inherit" size="small" onClick={loadDashboardData}>
          Tentar Novamente
        </Button>
      }>
        {error}
      </Alert>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
      <Box>
        {/* Drawer Administrativo */}
        <Drawer
          anchor="right"
          open={drawerOpen}
          onClose={toggleDrawer}
          PaperProps={{
            sx: {
              width: 320,
              backgroundColor: '#f8fafc',
              borderLeft: '1px solid #e2e8f0'
            }
          }}
        >
          <Box sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
              <Typography variant="h5" fontWeight="bold" color="primary">
                <AdminPanelSettings sx={{ mr: 1, verticalAlign: 'middle' }} />
                Administração
              </Typography>
              <IconButton onClick={toggleDrawer} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
            
            <Typography variant="body2" color="text.secondary" mb={3}>
              Acesse as funcionalidades administrativas do sistema
            </Typography>

            <List>
              {availableMenuItems.map((item) => (
                <ListItem key={item.path} disablePadding sx={{ mb: 1 }}>
                  <ListItemButton
                    onClick={() => handleNavigate(item.path)}
                    sx={{
                      borderRadius: 2,
                      '&:hover': {
                        backgroundColor: 'primary.light',
                        color: 'white',
                        '& .MuiListItemIcon-root': {
                          color: 'white'
                        }
                      }
                    }}
                  >
                    <ListItemIcon>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      secondary={item.description}
                      primaryTypographyProps={{ fontWeight: 'medium' }}
                      secondaryTypographyProps={{ fontSize: '0.75rem' }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>

            {availableMenuItems.length === 0 && (
              <Box textAlign="center" py={4}>
                <Typography variant="body2" color="text.secondary">
                  Sem permissões administrativas
                </Typography>
              </Box>
            )}
          </Box>
        </Drawer>

        {/* FAB para abrir o drawer (apenas se tiver acesso) */}
        {hasAdminAccess && (
          <Fab
            color="primary"
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              zIndex: 1000
            }}
            onClick={toggleDrawer}
          >
            <Tooltip title="Painel Administrativo" placement="left">
              <AdminPanelSettings />
            </Tooltip>
          </Fab>
        )}

        {/* Header */}
        <Box mb={4}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box>
              <Typography variant="h4" component="h1" gutterBottom sx={{ 
                fontWeight: 700, 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1
              }}>
                📊 HS-BIsys Dashboard
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Análise completa de dados de vendas e performance em tempo real
              </Typography>
            </Box>
            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                onClick={loadDashboardData}
                startIcon={<Refresh />}
                disabled={loading}
                size="small"
              >
                Atualizar
              </Button>
            </Box>
          </Box>
          
          {/* Tabs Navigation */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs 
              value={currentTab} 
              onChange={(event, newValue) => setCurrentTab(newValue)}
              aria-label="Dashboard tabs"
            >
              <Tab 
                label="📊 Dashboard Analytics" 
                icon={<FilterList />} 
                iconPosition="start"
              />
              <Tab 
                label="🔍 Monitoramento Webhooks" 
                icon={<Monitor />} 
                iconPosition="start"
              />
              <Tab 
                label="📂 Upload CSV" 
                icon={<Upload />} 
                iconPosition="start"
              />
            </Tabs>
          </Box>
        </Box>

        {/* Tab Content */}
        {currentTab === 0 ? (
          <Box>
            {/* Existing Dashboard Content */}
            {/* Filtros */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center">
              <FilterList sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Filtros Avançados</Typography>
            </Box>
            {(filters.startDate || filters.endDate || filters.productTitle || filters.utmSource || filters.utmCampaign || filters.utmMedium) && (
              <Chip 
                label="Filtros Aplicados" 
                color="primary" 
                variant="outlined"
                size="small"
              />
            )}
          </Box>
          
          <Grid container spacing={3}>
            {/* Primeira linha - Filtros de data e produto */}
            <Grid item xs={12} md={6} lg={3}>
              <DatePicker
                label="Data Inicial"
                value={filters.startDate}
                onChange={(value) => setFilters({ ...filters, startDate: value })}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </Grid>
            
            <Grid item xs={12} md={6} lg={3}>
              <DatePicker
                label="Data Final"
                value={filters.endDate}
                onChange={(value) => setFilters({ ...filters, endDate: value })}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </Grid>
            
            <Grid item xs={12} md={8} lg={4}>
              <AsyncSearchSelect
                label="Produto"
                value={filters.productId}
                selectedLabel={filters.productTitle}
                onChange={(id, option) => {
                  setFilters({ ...filters, productId: id, productTitle: option?.label || filters.productTitle });
                }}
                fetchPage={({ q, offset, limit }) => financeService.searchProducts({ q, offset, limit })}
                placeholder="Buscar produto..."
              />
            </Grid>

            <Grid item xs={12} md={4} lg={2}>
              <Box display="flex" gap={1} height="100%">
                <Button
                  variant="contained"
                  onClick={handleApplyFilters}
                  startIcon={<Refresh />}
                  disabled={loading}
                  fullWidth
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    minHeight: '56px'
                  }}
                >
                  Aplicar
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleClearFilters}
                  disabled={loading}
                  sx={{ minHeight: '56px' }}
                >
                  Limpar
                </Button>
              </Box>
            </Grid>

            {/* Segunda linha - Filtros UTM */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, mt: 1 }}>
                📈 Filtros de Marketing UTM
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="UTM Source"
                value={filters.utmSource}
                onChange={(e) => setFilters({ ...filters, utmSource: e.target.value })}
                placeholder="Ex: google, facebook, instagram"
                helperText="Origem do tráfego"
                size="small"
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="UTM Campaign"
                value={filters.utmCampaign}
                onChange={(e) => setFilters({ ...filters, utmCampaign: e.target.value })}
                placeholder="Ex: promocao-marco, black-friday"
                helperText="Nome da campanha"
                size="small"
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="UTM Medium"
                value={filters.utmMedium}
                onChange={(e) => setFilters({ ...filters, utmMedium: e.target.value })}
                placeholder="Ex: cpc, email, social"
                helperText="Meio de marketing"
                size="small"
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Mensagem quando não há dados */}
        {dashboardData && !dashboardData.overall && (
          <Alert severity="info" sx={{ mb: 4 }}>
            <Box>
              <Typography variant="h6" gutterBottom>
                Nenhum dado encontrado
              </Typography>
              <Typography variant="body2">
                Não há dados para os filtros selecionados. Tente ajustar o período ou remover alguns filtros.
              </Typography>
            </Box>
          </Alert>
        )}

        {/* Cards de estatísticas principais */}
        {dashboardData?.overall && (
          <Grid container spacing={2} mb={4}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                color: 'white',
                borderRadius: 3,
                boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
                transition: 'transform 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 40px rgba(102, 126, 234, 0.4)',
                }
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                        {formatNumber(dashboardData.overall.total_sales)}
                      </Typography>
                      <Typography variant="body1" sx={{ opacity: 0.9 }}>Total de Vendas</Typography>
                    </Box>
                    <Box sx={{ 
                      background: 'rgba(255,255,255,0.2)', 
                      borderRadius: '50%', 
                      p: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <ShoppingCart sx={{ fontSize: 32 }} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', 
                color: 'white',
                borderRadius: 3,
                boxShadow: '0 8px 32px rgba(240, 147, 251, 0.3)',
                transition: 'transform 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 40px rgba(240, 147, 251, 0.4)',
                }
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                        {formatCurrency(dashboardData.overall.total_revenue)}
                      </Typography>
                      <Typography variant="body1" sx={{ opacity: 0.9 }}>Receita Total</Typography>
                    </Box>
                    <Box sx={{ 
                      background: 'rgba(255,255,255,0.2)', 
                      borderRadius: '50%', 
                      p: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <AttachMoney sx={{ fontSize: 32 }} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', 
                color: 'white',
                borderRadius: 3,
                boxShadow: '0 8px 32px rgba(79, 172, 254, 0.3)',
                transition: 'transform 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 40px rgba(79, 172, 254, 0.4)',
                }
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                        {formatCurrency(dashboardData.overall.average_ticket)}
                      </Typography>
                      <Typography variant="body1" sx={{ opacity: 0.9 }}>Ticket Médio</Typography>
                    </Box>
                    <Box sx={{ 
                      background: 'rgba(255,255,255,0.2)', 
                      borderRadius: '50%', 
                      p: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <TrendingUp sx={{ fontSize: 32 }} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ 
                background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', 
                color: 'white',
                borderRadius: 3,
                boxShadow: '0 8px 32px rgba(67, 233, 123, 0.3)',
                transition: 'transform 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 40px rgba(67, 233, 123, 0.4)',
                }
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                        {formatNumber(dashboardData.overall.total_visitors || 0)}
                      </Typography>
                      <Typography variant="body1" sx={{ opacity: 0.9 }}>Visitantes</Typography>
                    </Box>
                    <Box sx={{ 
                      background: 'rgba(255,255,255,0.2)', 
                      borderRadius: '50%', 
                      p: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <People sx={{ fontSize: 32 }} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Análise de Origem de Tráfego */}
        {dashboardData?.traffic_sources?.length > 0 && (
          <Card sx={{ mb: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ p: 3, borderBottom: '1px solid #f0f0f0' }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                      📊 Análise de Origem de Tráfego
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Performance das campanhas e fontes de tráfego
                    </Typography>
                  </Box>
                  <Chip 
                    label={`${dashboardData.traffic_sources.length} fontes`}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                </Box>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                      <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Fonte</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Campanha</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Meio</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: 'text.primary' }}>Vendas</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: 'text.primary' }}>Receita</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: 'text.primary' }}>Participação</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dashboardData.traffic_sources.slice(0, 10).map((source, index) => (
                      <TableRow key={index} sx={{ 
                        '&:hover': { backgroundColor: '#f8f9fa' },
                        '&:nth-of-type(even)': { backgroundColor: '#fafafa' }
                      }}>
                        <TableCell>
                          <Chip 
                            label={source.utm_source || 'organico'} 
                            size="small" 
                            variant="outlined"
                            sx={{ fontWeight: 500 }}
                          />
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>
                          {source.utm_campaign || 'N/A'}
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>
                          {source.utm_medium || 'N/A'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 500 }}>
                          {formatNumber(source.total_sales)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: 'success.main' }}>
                          {formatCurrency(source.total_revenue)}
                        </TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={`${(source.percentage_share || 0).toFixed(1)}%`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

        {/* Análise Geográfica */}
        {dashboardData?.geographic?.length > 0 && (
          <Card sx={{ mb: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ p: 3, borderBottom: '1px solid #f0f0f0' }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                      🌍 Análise Geográfica por Estado
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Distribuição de vendas por localização geográfica
                    </Typography>
                  </Box>
                  <Chip 
                    label={`${dashboardData.geographic.length} estados`}
                    color="secondary"
                    variant="outlined"
                    size="small"
                  />
                </Box>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                      <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Estado</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Região</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: 'text.primary' }}>Vendas</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: 'text.primary' }}>Receita</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: 'text.primary' }}>Ticket Médio</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: 'text.primary' }}>Participação</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dashboardData.geographic.slice(0, 10).map((state, index) => (
                      <TableRow key={index} sx={{ 
                        '&:hover': { backgroundColor: '#f8f9fa' },
                        '&:nth-of-type(even)': { backgroundColor: '#fafafa' }
                      }}>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <Chip 
                              label={state.state_code} 
                              size="small" 
                              color="secondary"
                              variant="outlined"
                              sx={{ mr: 1, fontWeight: 600 }} 
                            />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {state.state_name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>{state.region}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 500 }}>
                          {formatNumber(state.total_sales)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: 'success.main' }}>
                          {formatCurrency(state.total_revenue)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 500, color: 'info.main' }}>
                          {formatCurrency(state.average_ticket)}
                        </TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={`${(state.percentage_share || 0).toFixed(1)}%`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

        {/* Seção de Gráficos e Visualizações Avançadas */}
        {dashboardData && (
          <Box sx={{ mt: 6 }}>
            <Divider sx={{ mb: 4 }}>
              <Typography variant="h5" sx={{ 
                fontWeight: 700, 
                color: 'primary.main',
                px: 2
              }}>
                📈 Análises Visuais Avançadas
              </Typography>
            </Divider>

            <Grid container spacing={4}>
              {/* 1. Análise de Origem do Tráfego - Gráfico de Pizza */}
              {dashboardData?.traffic_sources && Array.isArray(dashboardData.traffic_sources) && dashboardData.traffic_sources.length > 0 && (
                <Grid item xs={12} lg={6}>
                  <TrafficSourcePieChart 
                    data={dashboardData.traffic_sources} 
                    title="1. Distribuição de Origem do Tráfego (Dados Consolidados)"
                  />
                </Grid>
              )}

              {/* 2. Padrão de Vendas por Horário */}
              {dashboardData?.hourly_patterns && Array.isArray(dashboardData.hourly_patterns) && dashboardData.hourly_patterns.length > 0 && (
                <Grid item xs={12} lg={6}>
                  <HourlyPatternChart 
                    data={dashboardData.hourly_patterns} 
                    title="2. Padrão de Vendas por Horário"
                  />
                </Grid>
              )}

              {/* 3. Performance Financeira de Campanhas */}
              {dashboardData?.traffic_sources && Array.isArray(dashboardData.traffic_sources) && dashboardData.traffic_sources.length > 0 && (
                <Grid item xs={12}>
                  <CampaignPerformanceChart 
                    data={dashboardData.traffic_sources} 
                    title="3. Performance Financeira de Campanhas - Receita Total (R$)"
                  />
                </Grid>
              )}

              {/* 4. Análise Geográfica Completa */}
              {dashboardData?.geographic && Array.isArray(dashboardData.geographic) && dashboardData.geographic.length > 0 && (
                <Grid item xs={12}>
                  <GeographicChart 
                    data={dashboardData.geographic} 
                    title="4. Análise Geográfica Consolidada"
                  />
                </Grid>
              )}

              {/* 5. Análise Temporal */}
              {dashboardData?.monthly_breakdown && Array.isArray(dashboardData.monthly_breakdown) && dashboardData.monthly_breakdown.length > 0 && (
                <Grid item xs={12}>
                  <TemporalAnalysisChart 
                    monthlyData={dashboardData.monthly_breakdown} 
                    title="5. Análise Temporal Detalhada"
                    dashboardData={dashboardData}
                  />
                </Grid>
              )}

              {/* 6. Relatório Detalhado de Vendas Diárias */}
              {dashboardData?.monthly_breakdown && Array.isArray(dashboardData.monthly_breakdown) && dashboardData.monthly_breakdown.length > 0 && (
                <Grid item xs={12}>
                  <DailySalesTable 
                    monthlyData={dashboardData.monthly_breakdown} 
                    title="Relatório Completo de Vendas Diárias - Todo o Período"
                  />
                </Grid>
              )}
            </Grid>

            {/* Conclusões e Recomendações */}
            <Box sx={{ mt: 6 }}>
              <Divider sx={{ mb: 4 }}>
                <Typography variant="h5" sx={{ 
                  fontWeight: 700, 
                  color: 'success.main',
                  px: 2
                }}>
                  💡 Conclusões e Recomendações Consolidadas
                </Typography>
              </Divider>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card sx={{ 
                    borderLeft: '6px solid',
                    borderLeftColor: 'primary.main',
                    borderRadius: 2,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                  }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                        🎯 Principais Insights Consolidados
                      </Typography>
                      <Box component="ul" sx={{ pl: 2 }}>
                        <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                          <strong>Escala e Abrangência:</strong> Múltiplas origens fornecem dados robustos para análise
                        </Typography>
                        <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                          <strong>Padrões Temporais Otimizados:</strong> Estratégia temporal pode aumentar conversão em 20-30%
                        </Typography>
                        <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                          <strong>Campanhas de Alto ROI:</strong> Concentração de investimento nas top 5 campanhas recomendada
                        </Typography>
                        <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                          <strong>Segmentação Estratégica:</strong> Volume: 36-45 (184747 clientes) | Premium: 56-65 (produtos exclusivos)
                        </Typography>
                        <Typography component="li" variant="body2">
                          <strong>Expansão Geográfica Direcionada:</strong> 27 estados ativos, potencial em todos os 27
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card sx={{ 
                    borderLeft: '6px solid',
                    borderLeftColor: 'success.main',
                    borderRadius: 2,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                  }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'success.main' }}>
                        🚀 Recomendações Estratégicas
                      </Typography>
                      <Box component="ul" sx={{ pl: 2 }}>
                        <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                          <strong>Otimização Temporal:</strong> Concentrar campanhas entre 10h00 e 22h00
                        </Typography>
                        <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                          <strong>Investimento em Campanhas:</strong> Alocar 60% do budget nas top 5 campanhas por receita
                        </Typography>
                        <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                          <strong>Segmentação de Clientes:</strong> Estratégia volume para faixa 36-45 (promocionais) | Estratégia premium para faixa 56-65 (produtos exclusivos)
                        </Typography>
                        <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                          <strong>Expansão Geográfica:</strong> Fortalecer presença em BA (market share) | Expandir em AP (alto valor) | Mapear oportunidades nos estados com baixa representação
                        </Typography>
                        <Typography component="li" variant="body2">
                          <strong>Próximos Passos:</strong> Implementar tracking de campanhas por ROI em tempo real | Desenvolver dashboards para monitoramento de KPIs | Estabelecer metas específicas por segmento e região
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          </Box>
        )}

        {/* Botão para gerar relatório */}
        <Box display="flex" justifyContent="center" mt={6}>
          <Button
            variant="contained"
            size="large"
            startIcon={<Download />}
            onClick={handleGenerateReport}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              px: 4,
              py: 1.5,
            }}
          >
            Gerar Relatório PDF Completo
          </Button>
        </Box>
          </Box>
        ) : currentTab === 1 ? (
          <Box>
            {/* Webhook Monitoring Content */}
            <WebhookMonitoring />
          </Box>
        ) : (
          <Box>
            {/* CSV Upload Content */}
            <CSVUploadComponent />
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default DashboardPage;
